import { ReactNode, useId } from 'react';
import { makeStyles } from '@material-ui/core';
import GroupIcon from '@material-ui/icons/GroupOutlined';
import {
  CompoundEntityRef,
  Entity,
  RELATION_CHILD_OF,
  RELATION_HAS_MEMBER,
  RELATION_OWNER_OF,
  RELATION_PARENT_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Avatar, Link, LinkButton } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  entityRouteParams,
  entityRouteRef,
  getEntityRelations,
  useEntity,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import {
  EntityDisplayLinks,
  readableName,
} from '../../shared/EntityDisplayLink';
import { memberPicture } from '../../shared/avatar';
import { toMailtoHref } from '../../shared/links';
import { NAME_MIN_WIDTH } from '../../shared/table';
import { ByxMessageKey, useByxT } from '../../shared/translations';
import { useCatalogRootPath } from '../../shared/useCatalogRootPath';
import { openSans } from '../../theme/fonts';
import { byxScheme } from '../../theme/tokens';
import { ByxCard } from '../ByxCard';
import { ByxCardBoundary } from '../ByxCardBoundary';
import { isGroup } from '../entityKinds';
import {
  RelationColumns,
  RelationPanels,
  RelationSection,
} from '../RelationPanels';
import { useSquadMembers } from './squadMembers';

/**
 * The Squad page (Story 4.1, AC-8): Itens da squad in the main column — the
 * group's `ownerOf` relations, one table panel per kind — and Sobre in the
 * side column (Squad pai, Squads filhas, Membros, Contato). No relations
 * tree (AD-6).
 */

/** The kinds of Itens da squad, in order, with their Catálogo tab. */
export const SQUAD_SECTIONS: ReadonlyArray<
  RelationSection & { kind: string; viewKey: ByxMessageKey }
> = [
  {
    id: 'components',
    relation: RELATION_OWNER_OF,
    kind: 'component',
    titleKey: 'entity.squad.components',
    viewKey: 'entity.squad.viewComponents',
  },
  {
    id: 'apis',
    relation: RELATION_OWNER_OF,
    kind: 'api',
    titleKey: 'entity.squad.apis',
    viewKey: 'entity.squad.viewApis',
  },
  {
    id: 'resources',
    relation: RELATION_OWNER_OF,
    kind: 'resource',
    titleKey: 'entity.squad.resources',
    viewKey: 'entity.squad.viewResources',
  },
  {
    id: 'systems',
    relation: RELATION_OWNER_OF,
    kind: 'system',
    titleKey: 'entity.squad.systems',
    viewKey: 'entity.squad.viewSystems',
  },
];

/** Column plans of the UI spec: main column, and full width (no Sobre). */
export const SQUAD_COLUMNS: Record<'main' | 'full', RelationColumns> = {
  main: { name: NAME_MIN_WIDTH, type: 110, lifecycle: 120 },
  full: { name: NAME_MIN_WIDTH, type: 140, lifecycle: 140 },
};

/**
 * "Ver no catálogo" (AC-10): the Catálogo on the kind's tab, filtered by
 * the squad — the query shape the Catálogo itself writes (`kindSearch`).
 */
export function squadCatalogPath(
  catalogRoot: string,
  kind: string,
  groupRef: string,
): string {
  const params = new URLSearchParams();
  params.set('filters[kind]', kind);
  params.set('filters[owners]', groupRef);
  return `${catalogRoot}?${params}`;
}

/** The group's own Contato address as a `mailto:` link, when it is valid. */
export function squadContact(entity: Entity): string | undefined {
  const profile = entity.spec?.profile as { email?: unknown } | undefined;
  return toMailtoHref(profile?.email);
}

/** True when Sobre has a field: parent, children, members or contact. */
export function hasSquadAbout(entity: Entity): boolean {
  return (
    isGroup(entity) &&
    (getEntityRelations(entity, RELATION_CHILD_OF, { kind: 'group' }).length >
      0 ||
      getEntityRelations(entity, RELATION_PARENT_OF, { kind: 'group' }).length >
        0 ||
      getEntityRelations(entity, RELATION_HAS_MEMBER, { kind: 'user' }).length >
        0 ||
      !!squadContact(entity))
  );
}

const useStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  const oneLine = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as const;
  return {
    viewInCatalog: {
      height: 32,
      padding: theme.spacing(0, 1),
      fontSize: 13,
      fontWeight: 600,
      textTransform: 'none',
      color: scheme.accent,
      '&:hover': { backgroundColor: scheme.hover },
    },
    fields: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      margin: 0,
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    label: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '17px',
      color: scheme.mutedText,
    },
    single: {
      margin: theme.spacing(0.25, 0, 0),
      minWidth: 0,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '21px',
    },
    // A list value: the list's own 6px is the whole label gap (U3-4).
    listValue: {
      margin: 0,
      minWidth: 0,
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      margin: theme.spacing(0.75, 0, 0),
      padding: 0,
      listStyle: 'none',
      fontSize: 14,
      fontWeight: 500,
    },
    groupRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 32,
      minWidth: 0,
    },
    memberRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      minHeight: 32,
      minWidth: 0,
    },
    groupIcon: {
      flex: 'none',
      fontSize: 16,
      color: scheme.mutedText,
    },
    link: {
      ...oneLine,
      '& a': { color: scheme.accent },
    },
    contact: {
      ...oneLine,
      display: 'block',
      fontWeight: 500,
      color: scheme.accent,
    },
    hidden: { display: 'none' },
    avatarBox: {
      flex: 'none',
      display: 'inline-flex',
    },
    // The stock Avatar at 24px; initials in 11px 600 chipInk on chip. The
    // doubled selector wins over the stock size and colour classes.
    avatar: {
      '&&': {
        width: 24,
        height: 24,
        fontFamily: openSans,
        color: scheme.chipInk,
        backgroundColor: scheme.chip,
      },
    },
    avatarText: {
      '&&': {
        fontFamily: openSans,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '24px',
        letterSpacing: 0,
      },
    },
  };
});

function SquadItemsBody() {
  const { entity } = useEntity();
  const { t } = useByxT();
  const classes = useStyles();
  const catalogRoot = useCatalogRootPath();
  const groupRef = stringifyEntityRef(entity);
  const columns = hasSquadAbout(entity)
    ? SQUAD_COLUMNS.main
    : SQUAD_COLUMNS.full;
  return (
    <RelationPanels
      entity={entity}
      title={t('entity.squad.items')}
      sections={SQUAD_SECTIONS}
      columns={columns}
      pageStates={{
        error: t('entity.squad.error'),
        empty: t('entity.squad.empty'),
      }}
      renderSectionAction={section => (
        <LinkButton
          to={squadCatalogPath(catalogRoot, section.kind ?? '', groupRef)}
          variant="text"
          className={classes.viewInCatalog}
          aria-label={t(
            SQUAD_SECTIONS.find(item => item.id === section.id)?.viewKey ??
              'entity.squad.viewInCatalog',
          )}
        >
          {t('entity.squad.viewInCatalog')}
        </LinkButton>
      )}
    />
  );
}

/** Itens da squad (UI spec): one table panel per kind the squad owns. */
export function SquadItemsCard() {
  const { t } = useByxT();
  return (
    <ByxCardBoundary title={t('entity.squad.items')} variant="table">
      <SquadItemsBody />
    </ByxCardBoundary>
  );
}

/** A squad link: the group icon, then the display name (one line). */
function SquadLink({ entityRef }: Readonly<{ entityRef: CompoundEntityRef }>) {
  const classes = useStyles();
  return (
    <span className={classes.groupRow}>
      <GroupIcon aria-hidden className={classes.groupIcon} />
      <EntityDisplayLinks
        entityRefs={[entityRef]}
        defaultKind="group"
        className={classes.link}
      />
    </span>
  );
}

/**
 * One member: the stock Avatar (decorative; the name carries the meaning)
 * and the display name linking to the User page. Display name only, never
 * the member's email or ids (MC-2).
 */
function Member(
  props: Readonly<{ entityRef: CompoundEntityRef; picture?: string }>,
) {
  const { entityRef, picture } = props;
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const { primaryTitle } = useEntityPresentation(entityRef, {
    defaultKind: 'user',
  });
  const name = readableName(primaryTitle, entityRef);
  return (
    <li className={classes.memberRow}>
      <span className={classes.avatarBox} aria-hidden="true">
        <Avatar
          displayName={name}
          picture={memberPicture(picture)}
          classes={{ avatar: classes.avatar, avatarText: classes.avatarText }}
        />
      </span>
      <span className={classes.link} title={name}>
        <Link to={entityRoute(entityRouteParams(entityRef))}>{name}</Link>
      </span>
    </li>
  );
}

/**
 * Contato: the squad's own address (never a member's) as a `mailto:` link
 * through the core-components Link. Its accessible name is the address,
 * referenced with `aria-labelledby`, instead of the English "opens in a
 * new window" hint core-components adds to non-app links.
 */
function ContactLink(props: Readonly<{ href: string; email: string }>) {
  const classes = useStyles();
  const id = useId();
  return (
    <>
      <Link
        to={props.href}
        className={classes.contact}
        title={props.email}
        aria-labelledby={id}
        underline="none"
      >
        {props.email}
      </Link>
      <span id={id} className={classes.hidden}>
        {props.email}
      </span>
    </>
  );
}

function Field(
  props: Readonly<{
    label: string;
    children: ReactNode;
    list?: boolean;
  }>,
) {
  const classes = useStyles();
  return (
    <div className={classes.field}>
      <dt className={classes.label}>{props.label}</dt>
      {props.list ? (
        <dd className={classes.listValue}>
          <ul className={classes.list}>{props.children}</ul>
        </dd>
      ) : (
        <dd className={classes.single}>{props.children}</dd>
      )}
    </div>
  );
}

function SquadAboutBody() {
  const { entity } = useEntity();
  const { t } = useByxT();
  const classes = useStyles();
  const parents = getEntityRelations(entity, RELATION_CHILD_OF, {
    kind: 'group',
  });
  const children = getEntityRelations(entity, RELATION_PARENT_OF, {
    kind: 'group',
  });
  const members = getEntityRelations(entity, RELATION_HAS_MEMBER, {
    kind: 'user',
  });
  const pictures = useSquadMembers(members);
  const contact = squadContact(entity);
  const email = contact?.slice('mailto:'.length);

  if (!hasSquadAbout(entity)) {
    return null;
  }
  return (
    <ByxCard title={t('entity.about.title')}>
      <dl className={classes.fields}>
        {parents.length > 0 && (
          <Field label={t('entity.squad.parent')}>
            <SquadLink entityRef={parents[0]} />
          </Field>
        )}
        {children.length > 0 && (
          <Field label={t('entity.squad.children')} list>
            {children.map(ref => (
              <li key={stringifyEntityRef(ref)}>
                <SquadLink entityRef={ref} />
              </li>
            ))}
          </Field>
        )}
        {members.length > 0 && (
          <Field label={t('entity.squad.members')} list>
            {members.map(ref => {
              const key = stringifyEntityRef(ref);
              return (
                <Member key={key} entityRef={ref} picture={pictures.get(key)} />
              );
            })}
          </Field>
        )}
        {contact && email && (
          <Field label={t('entity.squad.contact')}>
            <ContactLink href={contact} email={email} />
          </Field>
        )}
      </dl>
    </ByxCard>
  );
}

/**
 * Sobre of a squad (UI spec): Squad pai · Squads filhas · Membros ·
 * Contato, one field per row; a field with no value is not rendered, and
 * the card is hidden (its filter) when no field has one.
 */
export function SquadAboutCard() {
  const { t } = useByxT();
  return (
    <ByxCardBoundary title={t('entity.about.title')}>
      <SquadAboutBody />
    </ByxCardBoundary>
  );
}
